import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { ArrowLeft, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import {
  MAX_LINE_LENGTH,
  cardOptions,
  defaultLine,
  drawCard,
  FESTIVAL_DAY_NAMES,
  festivalDayName,
  findOccasion,
  greetingChoices,
  loadCardFonts,
  occasionName,
  wishLine,
  type CardChoice,
  type FestivalDay,
  type FestivalDayId,
  type FestivalTiming,
  type Occasion,
  type OccasionId,
} from '../../lib/greetings';
import type { MessageKey } from '../../i18n';
import { useLanguage, useT } from '../../i18n';

interface GreetingSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Opens straight on this card's preview (e.g. "Wish them" on a birthday). */
  initialOccasion?: OccasionId | null;
  /**
   * …with this festival day's cards leading, even before its date (a Bhai
   * Dooj nudge the day before, D315).
   */
  initialDay?: FestivalDayId | null;
  /** Who it's for, in a direct chat: named on birthday and anniversary wishes. */
  recipientName?: string;
  /** Signs the card: "— Asha". */
  senderName: string;
  onSend: (card: CardChoice, line: string) => void;
}

const TIMING_LABELS: Record<FestivalTiming, MessageKey> = {
  today: 'chat.greeting.today',
  soon: 'chat.greeting.soon',
  belated: 'chat.greeting.belated',
};

// Thumbnails are drawn small (the sheet shows them about 104px wide).
const THUMB_PIXELS = 288;

// "Send a greeting" (D144): pick an occasion, then a design and a wish
// (D269), edit the line, send the card. The preview is the real card, drawn
// on a canvas at full size and shown scaled down, so what you see is what's
// sent. Festivals appear only around their days, first and wide; family
// occasions are always there (D176). Every day of a festival is offered
// while it's in its window (Dhanteras to Bhai Dooj), the likeliest day for
// today leading on the festival's days, so a table that's a day off where
// you are never hides a day's cards (D269). Nothing is remembered.
export function GreetingSheet({
  isOpen,
  onClose,
  initialOccasion,
  initialDay,
  recipientName,
  senderName,
  onSend,
}: GreetingSheetProps) {
  const phone = useBreakpointValue({ base: true, md: false }) ?? true;
  const t = useT();
  const [picked, setPicked] = useState<{
    occasion: Occasion;
    days: FestivalDay[];
    leads: boolean;
  } | null>(null);
  const [line, setLine] = useState('');
  // Worked out again each time the sheet opens, by this device's date.
  const [choices, setChoices] = useState(() => greetingChoices());

  const seasonFor = (
    occasion: Occasion,
    from: ReturnType<typeof greetingChoices>
  ) => from.seasonal.find((s) => s.occasion.id === occasion.id);

  useEffect(() => {
    if (!isOpen) return;
    const next = greetingChoices();
    setChoices(next);
    const start = findOccasion(initialOccasion);
    const season = start ? seasonFor(start, next) : undefined;
    let days = season?.days ?? [];
    let leads = season?.dayLeads ?? false;
    const asked = initialDay ? days.find((d) => d.id === initialDay) : null;
    if (asked) {
      days = [asked, ...days.filter((d) => d !== asked)];
      leads = true;
    }
    setPicked(start ? { occasion: start, days, leads } : null);
    setLine(start ? defaultLine(start, recipientName, days, leads) : '');
  }, [isOpen, initialOccasion, initialDay, recipientName]);

  const pick = (occasion: Occasion, days: FestivalDay[], leads: boolean) => {
    setPicked({ occasion, days, leads });
    setLine(defaultLine(occasion, recipientName, days, leads));
  };

  const content = picked ? (
    <CardPreview
      occasion={picked.occasion}
      days={picked.days}
      leads={picked.leads}
      line={line}
      onLineChange={setLine}
      recipientName={recipientName}
      senderName={senderName}
      onBack={() => setPicked(null)}
      onSend={onSend}
    />
  ) : (
    <Stack spacing={4}>
      <Stack spacing="6px">
        <Heading as="h2" fontSize="24px" letterSpacing="-0.01em">
          {t('chat.greeting.title')}
        </Heading>
        <Text fontSize="16px" color="ink.body">
          {t('chat.greeting.pick')}
        </Text>
      </Stack>
      {choices.seasonal.length > 0 && (
        <Stack as="ul" listStyleType="none" spacing="10px">
          {choices.seasonal.map(
            ({ occasion: item, timing, days, dayLeads }) => {
              const look = cardOptions(item, days, dayLeads).cards[0]!.design;
              return (
                <Box as="li" key={item.id}>
                  <Box
                    as="button"
                    type="button"
                    onClick={() => pick(item, days, dayLeads)}
                    w="full"
                    minH="88px"
                    px={5}
                    py={3}
                    borderRadius="20px"
                    bg={look.ground}
                    color={look.ink}
                    display="flex"
                    alignItems="center"
                    gap={4}
                    textAlign="left"
                    boxShadow="inset 0 0 0 1px rgba(31, 29, 27, 0.08)"
                    _hover={{ transform: 'translateY(-1px)' }}
                    _focusVisible={{ boxShadow: 'outline' }}
                  >
                    <Text
                      as="span"
                      fontSize="44px"
                      lineHeight={1}
                      aria-hidden="true"
                    >
                      {look.motif[0]}
                    </Text>
                    <Box as="span" display="block">
                      <Text
                        as="span"
                        display="block"
                        fontSize="19px"
                        fontWeight={800}
                        lineHeight={1.2}
                      >
                        {t(TIMING_LABELS[timing], { name: occasionName(item) })}
                      </Text>
                      {dayLeads && days[0] && (
                        <Text
                          as="span"
                          display="block"
                          fontSize="15px"
                          fontWeight={600}
                          opacity={0.85}
                          mt="2px"
                        >
                          {festivalDayName(days[0])}
                        </Text>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            }
          )}
        </Stack>
      )}
      {choices.seasonal.length > 0 && (
        <Text
          fontSize="13px"
          fontWeight={700}
          letterSpacing="0.06em"
          textTransform="uppercase"
          color="muted"
          mb="-6px"
        >
          {t('chat.greeting.anyDay')}
        </Text>
      )}
      <Grid
        as="ul"
        listStyleType="none"
        templateColumns="repeat(3, minmax(0, 1fr))"
        gap="10px"
        aria-label={t('chat.greeting.occasions')}
      >
        {choices.always.map((item) => {
          const look = item.designs[0]!;
          return (
            <Box as="li" key={item.id}>
              <Box
                as="button"
                type="button"
                onClick={() => pick(item, [], false)}
                w="full"
                minH="88px"
                px={2}
                py={3}
                borderRadius="20px"
                bg={look.ground}
                color={look.ink}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap="6px"
                boxShadow="inset 0 0 0 1px rgba(31, 29, 27, 0.08)"
                _hover={{ transform: 'translateY(-1px)' }}
                _focusVisible={{ boxShadow: 'outline' }}
              >
                <Text
                  as="span"
                  fontSize="32px"
                  lineHeight={1}
                  aria-hidden="true"
                >
                  {look.motif[0]}
                </Text>
                <Text
                  as="span"
                  fontSize="14px"
                  fontWeight={700}
                  lineHeight={1.15}
                  maxW="full"
                  // Long words break rather than spill out of a tile on a 360px phone.
                  sx={{ hyphens: 'auto', overflowWrap: 'anywhere' }}
                  textAlign="center"
                >
                  {occasionName(item)}
                </Text>
              </Box>
            </Box>
          );
        })}
      </Grid>
      <Button
        h="56px"
        fontSize="17px"
        variant="outline"
        colorScheme="gray"
        borderColor="line"
        bg="surface"
        onClick={onClose}
      >
        {t('common.cancel')}
      </Button>
    </Stack>
  );

  if (phone) {
    return (
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay bg="rgba(31, 29, 27, 0.5)" />
        <DrawerContent
          borderTopRadius="28px"
          bg="surface"
          aria-label={t('chat.greeting.title')}
          maxH="92dvh"
          pb="max(env(safe-area-inset-bottom), 20px)"
        >
          <DrawerBody px={5} pt="10px" pb={0} overflowY="auto">
            <Box
              w="40px"
              h="5px"
              borderRadius="3px"
              bg="line"
              mx="auto"
              mb={4}
              aria-hidden="true"
            />
            {content}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent
        mx={5}
        borderRadius="28px"
        bg="surface"
        aria-label={t('chat.greeting.title')}
      >
        <ModalBody px={5} py={6}>
          {content}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function CardPreview({
  occasion,
  days,
  leads,
  line,
  onLineChange,
  recipientName,
  senderName,
  onBack,
  onSend,
}: {
  occasion: Occasion;
  days: FestivalDay[];
  leads: boolean;
  line: string;
  onLineChange: (line: string) => void;
  recipientName?: string;
  senderName: string;
  onBack: () => void;
  onSend: (card: CardChoice, line: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const t = useT();
  const { cards, wishes } = useMemo(
    () => cardOptions(occasion, days, leads),
    [occasion, days, leads]
  );
  const [index, setIndex] = useState(0);
  const card = cards[Math.min(index, cards.length - 1)]!;
  // Named after the chosen design's day ("Lakshmi Puja card") or the festival.
  const cardDay = days.find((day) => day.id === card.day);
  const name = cardDay ? festivalDayName(cardDay) : occasionName(occasion);
  const [fontsReady, setFontsReady] = useState(false);
  // Any wish with ", {name}" names them in a direct chat (D269, D315).
  const recipient = recipientName;
  const lines = useMemo(
    () => wishes.map((wish) => wishLine(wish, recipient)),
    [wishes, recipient]
  );
  // The chips: the chosen design's own wishes first (D269).
  const chips = useMemo(() => {
    const own = card.wishes.map((wish) => wishLine(wish, recipient));
    return [...own, ...lines.filter((l) => !own.includes(l))];
  }, [card, lines, recipient]);
  const language = useLanguage();
  // Thumbnails follow the line a moment later, not on every keystroke.
  const [thumbLine, setThumbLine] = useState(line);
  useEffect(() => {
    const timer = setTimeout(() => setThumbLine(line), 150);
    return () => clearTimeout(timer);
  }, [line]);

  // Picking a design for another day ("BHAI DOOJ") brings that day's first
  // wish, unless the line was edited: a ready-made or empty line follows.
  const pickDesign = (next: number) => {
    const chosen = cards[next];
    setIndex(next);
    if (!chosen || chosen.day === card.day) return;
    const untouched = line.trim() === '' || lines.includes(line);
    if (untouched && chosen.wishes[0]) {
      onLineChange(wishLine(chosen.wishes[0], recipient));
    }
  };

  useEffect(() => {
    setIndex(0);
  }, [occasion, days, leads]);

  useEffect(() => {
    let cancelled = false;
    void loadCardFonts(
      `${lines.join(' ')} ${cards.map((c) => c.label).join(' ')} ${senderName}`
    ).then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [lines, cards, senderName]);

  // Redrawn as you type; drawn again once the fonts arrive.
  useEffect(() => {
    if (canvasRef.current) {
      drawCard(canvasRef.current, card, line, senderName);
    }
  }, [card, line, senderName, fontsReady]);

  const send = () => onSend(card, line.trim() || card.fallback);

  return (
    <Stack spacing={4}>
      <HStack spacing={2}>
        <IconButton
          aria-label={t('chat.greeting.another')}
          icon={<ArrowLeft size={20} />}
          variant="ghost"
          colorScheme="gray"
          minW="44px"
          h="44px"
          onClick={onBack}
        />
        <Heading as="h2" fontSize="22px" letterSpacing="-0.01em">
          {t('chat.greeting.card', { name })}
        </Heading>
      </HStack>
      <Box
        as="canvas"
        ref={canvasRef}
        w="full"
        maxW={{ base: '280px', md: '340px' }}
        mx="auto"
        h="auto"
        display="block"
        aspectRatio={1}
        borderRadius="20px"
        boxShadow="0 12px 24px -12px rgba(31, 29, 27, 0.4)"
        role="img"
        aria-label={t('chat.greeting.cardLabel', {
          name,
          line: line.trim() || card.fallback,
        })}
      />
      <DesignStrip
        cards={cards}
        index={index}
        onIndex={pickDesign}
        occasion={occasion}
        line={thumbLine}
        senderName={senderName}
        fontsReady={fontsReady}
      />
      <Stack spacing={2}>
        <Text id="greeting-wishes" fontSize="15px" fontWeight={700}>
          {t('chat.greeting.wishes')}
        </Text>
        <ScrollRow as="ul" labelledBy="greeting-wishes" gap="8px">
          {chips.map((wish) => (
            <Box as="li" key={wish} flexShrink={0}>
              <Button
                size="sm"
                h="40px"
                px={4}
                borderRadius="full"
                variant={wish === line ? 'solid' : 'outline'}
                colorScheme={wish === line ? 'brand' : 'gray'}
                borderColor="line"
                fontWeight={600}
                maxW="260px"
                aria-pressed={wish === line}
                title={wish}
                onClick={() => onLineChange(wish)}
              >
                <Text as="span" noOfLines={1}>
                  {chipText(wish, language)}
                </Text>
              </Button>
            </Box>
          ))}
        </ScrollRow>
      </Stack>
      <FormControl>
        <FormLabel fontSize="15px" fontWeight={700} mb={1}>
          {t('chat.greeting.yours')}
        </FormLabel>
        <Input
          h="52px"
          value={line}
          maxLength={MAX_LINE_LENGTH}
          onChange={(event) => onLineChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') send();
          }}
        />
      </FormControl>
      <Button
        h="56px"
        fontSize="17px"
        leftIcon={<Send size={18} />}
        onClick={send}
      >
        {t('chat.greeting.send')}
      </Button>
    </Stack>
  );
}

// A row of the designs, drawn small with the line as it stands. It scrolls
// sideways by touch; with a keyboard it's one stop, and the arrow keys move
// between designs (a radio group).
function DesignStrip({
  cards,
  index,
  onIndex,
  occasion,
  line,
  senderName,
  fontsReady,
}: {
  cards: CardChoice[];
  index: number;
  onIndex: (index: number) => void;
  occasion: Occasion;
  line: string;
  senderName: string;
  fontsReady: boolean;
}) {
  const t = useT();
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const last = cards.length - 1;
    const next =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? Math.min(last, index + 1)
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? Math.max(0, index - 1)
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    onIndex(next);
    buttons.current[next]?.focus();
  };

  // Keeps the chosen design in view as the arrow keys move along.
  useEffect(() => {
    buttons.current[index]?.scrollIntoView?.({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'smooth',
    });
  }, [index]);

  return (
    <Stack spacing={2}>
      <Text id="greeting-designs" fontSize="15px" fontWeight={700}>
        {t('chat.greeting.designs')}
      </Text>
      <ScrollRow
        role="radiogroup"
        labelledBy="greeting-designs"
        onKeyDown={move}
        gap="10px"
        snap
      >
        {cards.map((card, i) => (
          <Box
            key={card.design.id}
            as="button"
            type="button"
            role="radio"
            aria-checked={i === index}
            aria-label={t('chat.greeting.design', {
              name: card.day
                ? t(FESTIVAL_DAY_NAMES[card.day])
                : occasionName(occasion),
              n: i + 1,
              total: cards.length,
            })}
            tabIndex={i === index ? 0 : -1}
            ref={(el: HTMLButtonElement | null) => {
              buttons.current[i] = el;
            }}
            onClick={() => onIndex(i)}
            flexShrink={0}
            w="104px"
            h="104px"
            p={0}
            borderRadius="16px"
            overflow="hidden"
            scrollSnapAlign="start"
            boxShadow={
              i === index
                ? '0 0 0 3px var(--chakra-colors-accent)'
                : 'inset 0 0 0 1px rgba(31, 29, 27, 0.12)'
            }
            m="3px"
            _focusVisible={{
              outline: '2px solid',
              outlineColor: 'ink',
              outlineOffset: '4px',
            }}
          >
            <Thumbnail
              card={card}
              line={line}
              senderName={senderName}
              fontsReady={fontsReady}
            />
          </Box>
        ))}
      </ScrollRow>
    </Stack>
  );
}

/** A chip's words: the Hindi half in Hindi, where the wish has one (D146). */
function chipText(wish: string, language: string): string {
  const [english, ...rest] = wish.split(' · ');
  const hindi = rest.join(' · ');
  return language === 'hi' && /[\u0900-\u097F]/.test(hindi)
    ? hindi
    : (english ?? wish);
}

// A row that scrolls sideways: by touch on a phone; from `md` up, with ‹ ›
// buttons too, since a mouse wheel scrolls up and down (D269).
function ScrollRow({
  as = 'div',
  role,
  labelledBy,
  onKeyDown,
  gap,
  snap = false,
  children,
}: {
  as?: 'div' | 'ul';
  role?: string;
  labelledBy: string;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  gap: string;
  snap?: boolean;
  children: ReactNode;
}) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const by = (direction: 1 | -1) =>
    ref.current?.scrollBy({
      left: direction * ref.current.clientWidth * 0.8,
      behavior: 'smooth',
    });
  const arrow = (direction: 1 | -1) => (
    <IconButton
      aria-label={t(
        direction < 0 ? 'chat.greeting.scrollBack' : 'chat.greeting.scrollOn'
      )}
      icon={
        direction < 0 ? <ChevronLeft size={18} /> : <ChevronRight size={18} />
      }
      display={{ base: 'none', md: 'inline-flex' }}
      variant="outline"
      colorScheme="gray"
      borderColor="line"
      bg="surface"
      size="sm"
      minW="36px"
      h="36px"
      borderRadius="full"
      flexShrink={0}
      // The arrow keys and Tab already move along; these are for the mouse.
      tabIndex={-1}
      onClick={() => by(direction)}
    />
  );
  return (
    <HStack spacing={2} align="center" mx={{ base: -5, md: 0 }}>
      {arrow(-1)}
      <Box
        ref={ref}
        as={as}
        role={role}
        aria-labelledby={labelledBy}
        onKeyDown={onKeyDown}
        listStyleType="none"
        display="flex"
        gap={gap}
        flex={1}
        minW={0}
        overflowX="auto"
        px={{ base: 5, md: 0 }}
        py={1}
        sx={{
          ...(snap
            ? { scrollSnapType: 'x mandatory', scrollPaddingInline: '20px' }
            : {}),
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {children}
      </Box>
      {arrow(1)}
    </HStack>
  );
}

function Thumbnail({
  card,
  line,
  senderName,
  fontsReady,
}: {
  card: CardChoice;
  line: string;
  senderName: string;
  fontsReady: boolean;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (ref.current)
      drawCard(ref.current, card, line, senderName, THUMB_PIXELS);
  }, [card, line, senderName, fontsReady]);
  return (
    <Box
      as="canvas"
      ref={ref}
      w="full"
      h="full"
      display="block"
      aria-hidden="true"
    />
  );
}
